import { useMemo, useState } from 'react';
import { SquiggleItem, SquiggleTypes } from '../types/types';
import { makeLookupTreeFromItems, resolveAllImplicitLinks } from '../biz/linkFinder';
import GraphCanvas from './GraphCanvas';
import './GraphView.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGears, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { useGraphViewSettingsContext } from '../context/GraphViewSettingsContext';
import { useAdventureDataContext } from '../context/AdventureDataContext';
import { defaultLayoutSettings } from './graphViewSettings';
import type { GraphLinkMode, LayoutSettings } from './graphViewSettings';
import { DirectedEdge, findNeighbourExplicitLinks, findNeighbourHierarchyLinks, findNeighbourImplicitLinks, ItemId } from './neighbours';

const POSITION_HASH_OFFSET = 2166136261;
const POSITION_HASH_PRIME = 16777619;

function hashString(value: string, salt: number) {
  let hash = (POSITION_HASH_OFFSET ^ salt) >>> 0;

  for (let i = 0; i < value.length; i++) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, POSITION_HASH_PRIME) >>> 0;
  }

  return hash;
}

function getSeededNodePosition(nodeId: string, nodeCount: number) {
  const spread = Math.max(2000, Math.ceil(Math.sqrt(nodeCount)) * 300);
  const xRatio = hashString(nodeId, 0x9e3779b9) / 0xffffffff;
  const yRatio = hashString(nodeId, 0x85ebca6b) / 0xffffffff;

  return {
    x: (xRatio - 0.5) * spread,
    y: (yRatio - 0.5) * spread
  };
}

function getSeededOffset(nodeId: string, spread: number) {
  const xRatio = hashString(nodeId, 0x27d4eb2d) / 0xffffffff;
  const yRatio = hashString(nodeId, 0x165667b1) / 0xffffffff;

  return {
    x: (xRatio - 0.5) * spread,
    y: (yRatio - 0.5) * spread
  };
}

function getSeededEdgeColor(edgeId: string) {
  const lightness = 24 + Math.round((hashString(edgeId, 0xc2b2ae35) / 0xffffffff) * 28);
  const hue = Math.round((hashString(edgeId, 0xa7c63b4a) / 0xffffffff) * 360);
  return `hsl(${hue}, 10%, ${lightness}%)`;
}

const itemTypeColors: Record<SquiggleTypes, string> = {
  quest: '#284',
  personororg: '#d92',
  place: '#b54',
  miscitem: '#58a',
  logentry: '#ccc',
  system: '#777'
};

type Edge = { data: { id: string; source: ItemId; target: ItemId; color: string }; classes: string };

const findNonHierarchyExplicitLinks = (sortedItems: SquiggleItem[], openItemIds: Set<ItemId>, allDirectedEdges: Map<string, DirectedEdge>) => {
  for (const sourceItem of sortedItems) {
    const sortedLinks = [...sourceItem.links].sort((left, right) => left.localeCompare(right)) as ItemId[];

    for (const targetId of sortedLinks) {
      if (!openItemIds.has(targetId) || targetId === sourceItem.id) {
        continue;
      }
      const edgeKey = `${sourceItem.id}->${targetId}`;
      allDirectedEdges.set(edgeKey, { source: sourceItem.id as ItemId, target: targetId, implicit: false });
    }
  }
};

const findImplicitLinks = (sortedItems: SquiggleItem[], openItemIds: Set<ItemId>, allDirectedEdges: Map<string, DirectedEdge>, itemLookupTree: ReturnType<typeof makeLookupTreeFromItems>) => {
  for (const sourceItem of sortedItems) {
    const sortedImplicitLinks = resolveAllImplicitLinks(itemLookupTree, sourceItem.text, sourceItem.id)
      .sort((left, right) => left.id.localeCompare(right.id));

    for (const { id } of sortedImplicitLinks) {
      const targetId = id as ItemId;
      if (!openItemIds.has(targetId) || targetId === sourceItem.id) {
        continue;
      }
      const edgeKey = `${sourceItem.id}->${targetId}`;
      if (!allDirectedEdges.has(edgeKey)) {
        allDirectedEdges.set(edgeKey, { source: sourceItem.id as ItemId, target: targetId, implicit: true });
      }
    }
  }
};

const findHierarchyLinks = (sortedItems: SquiggleItem[], openItemIds: Set<ItemId>, allDirectedEdges: Map<string, DirectedEdge>) => {
  for (const item of sortedItems) {
    if (item.parent && openItemIds.has(item.parent as ItemId)) {
      const edgeKey = `${item.id}->${item.parent}`;
      allDirectedEdges.set(edgeKey, { source: item.id as ItemId, target: item.parent as ItemId, implicit: false });
    }
  }
};

const constructNeighbourLinks = (
  allItems: SquiggleItem[],
  openItems: SquiggleItem[],
  openItemIds: Set<ItemId>,
  itemById: Map<string, SquiggleItem>,
  childrenByParentId: Map<string, SquiggleItem[]>,
  itemLookupTree: ReturnType<typeof makeLookupTreeFromItems> | null,
  linkMode: GraphLinkMode,
  isVisible: (item: SquiggleItem) => boolean
) => {
  const neighbourDirectedEdges = new Map<string, DirectedEdge>();
  const neighbourItemIds = new Set<ItemId>();

  findNeighbourHierarchyLinks(openItems, openItemIds, itemById, childrenByParentId, isVisible, neighbourDirectedEdges, neighbourItemIds);

  if (linkMode !== 'parent-links') {
    findNeighbourExplicitLinks(openItems, openItemIds, itemById, isVisible, neighbourDirectedEdges, neighbourItemIds);

    if (linkMode === 'all-links' && itemLookupTree) {
      findNeighbourImplicitLinks(openItems, allItems, openItemIds, itemById, isVisible, itemLookupTree, neighbourDirectedEdges, neighbourItemIds);
    }
  }

  const edges: Edge[] = Array.from(neighbourDirectedEdges.entries()).map(([edgeKey, edge]) => ({
    data: {
      id: edgeKey,
      source: edge.source,
      target: edge.target,
      color: getSeededEdgeColor(edgeKey)
    },
    classes: ['neighbour', edge.implicit ? 'implicit' : ''].filter(Boolean).join(' ')
  }));

  const neighbourItems = Array.from(neighbourItemIds)
    .map((id) => itemById.get(id))
    .filter((item): item is SquiggleItem => item !== undefined);

  return { edges, neighbourItems };
};

const constructEdges = (sortedItems: SquiggleItem[], openItemIds: Set<ItemId>, itemLookupTree: ReturnType<typeof makeLookupTreeFromItems> | null, linkMode: GraphLinkMode): Edge[] => {
  const allDirectedEdges = new Map<string, DirectedEdge>();

  if (linkMode !== 'parent-links') {
    findNonHierarchyExplicitLinks(sortedItems, openItemIds, allDirectedEdges);

    if (linkMode === 'all-links' && itemLookupTree) {
      findImplicitLinks(sortedItems, openItemIds, allDirectedEdges, itemLookupTree);
    }
  }

  findHierarchyLinks(sortedItems, openItemIds, allDirectedEdges);

  // Detect bidirectional pairs and build final edge list
  const seenEdgePairs = new Set<string>();
  const edges: Edge[] = [];

  for (const [edgeKey, edge] of allDirectedEdges.entries()) {
    const reverseKey = `${edge.target}->${edge.source}`;
    const pairKey = [edge.source, edge.target].sort().join('|'); // canonical pair key

    if (seenEdgePairs.has(pairKey)) {
      continue; // Already added this bidirectional pair
    }

    const isBidirectional = allDirectedEdges.has(reverseKey);
    if (isBidirectional) {
      seenEdgePairs.add(pairKey);
    }

    const classes = [isBidirectional ? 'bidirectional' : '', edge.implicit ? 'implicit' : ''].filter(Boolean).join(' ');

    edges.push({
      data: {
        id: edgeKey,
        source: edge.source,
        target: edge.target,
        color: getSeededEdgeColor(edgeKey)
      },
      classes
    });
  }
  return edges;
};

const filterConnectedItems = (sortedItems: SquiggleItem[], edges: Edge[]) => {
  const connectedNodeIds = new Set<string>();
  for (const edge of edges) {
    connectedNodeIds.add(edge.data.source);
    connectedNodeIds.add(edge.data.target);
  }

  const connectedItems = sortedItems.filter((item) => connectedNodeIds.has(item.id));
  const unconnectedItems = sortedItems.filter((item) => !connectedNodeIds.has(item.id));
  return [connectedItems, unconnectedItems];
};

const findComponents = (items: SquiggleItem[], edges: Edge[]) => {
  const undirectedAdjacency = new Map<ItemId, Set<ItemId>>();
  for (const item of items) {
    undirectedAdjacency.set(item.id as ItemId, new Set());
  }
  for (const edge of edges) {
    undirectedAdjacency.get(edge.data.source)?.add(edge.data.target);
    undirectedAdjacency.get(edge.data.target)?.add(edge.data.source);
  }

  const components: ItemId[][] = [];
  const visited = new Set<ItemId>();
  for (const item of items) {
    if (visited.has(item.id as ItemId)) {
      continue;
    }

    const queue = [item.id as ItemId];
    const component: ItemId[] = [];
    visited.add(item.id as ItemId);

    while (queue.length > 0) {
      const current = queue.shift();
      if (!current) {
        continue;
      }

      component.push(current);
      const neighbors = undirectedAdjacency.get(current);
      if (!neighbors) {
        continue;
      }

      for (const neighbor of neighbors) {
        if (visited.has(neighbor)) {
          continue;
        }

        visited.add(neighbor);
        queue.push(neighbor);
      }
    }

    components.push(component.sort((left, right) => left.localeCompare(right)));
  }

  components.sort((left, right) => left[0].localeCompare(right[0]));
  return components;
};

const createNodesWithClusteredSpreadStartingPositions = (items: SquiggleItem[], edges: Edge[], neighbourItemIds: Set<ItemId>) => {
  const components = findComponents(items, edges);

  const componentCenterByNodeId = new Map<ItemId, { x: number; y: number }>();
  const componentSizeByNodeId = new Map<ItemId, number>();
  for (let index = 0; index < components.length; index++) {
    const component = components[index];
    const baseAngle = index/components.length * Math.PI * 2;
    const ringRadius = Math.max(160, Math.pow(component.length, 0.25) * 260);
    const center = {
      x: Math.cos(baseAngle) * ringRadius,
      y: Math.sin(baseAngle) * ringRadius
    };

    for (const nodeId of component) {
      componentCenterByNodeId.set(nodeId, center);
      componentSizeByNodeId.set(nodeId, component.length);
    }
  }

  const nodeCount = items.length;

  const nodes = items
    .map((item) => ({
      data: {
        id: item.id,
        label: item.name,
        color: itemTypeColors[item.type],
        archived: item.archived,
        neighbour: neighbourItemIds.has(item.id as ItemId)
      },
      position: (() => {
        const center = componentCenterByNodeId.get(item.id as ItemId) ?? { x: 0, y: 0 };
        const componentSize = componentSizeByNodeId.get(item.id as ItemId) ?? 1;
        const offset = getSeededOffset(item.id, Math.max(120, Math.pow(componentSize, 0.5) * 160));
        const fallback = getSeededNodePosition(item.id, nodeCount);

        return {
          x: center.x + offset.x + fallback.x * 0.08,
          y: center.y + offset.y + fallback.y * 0.08
        };
      })()
    }));
  return nodes;
};

function GraphView({ itemsOpen, selectedItemId, setSelectedItemId, openItem }: {
  itemsOpen: SquiggleItem[];
  selectedItemId: string | null;
  setSelectedItemId: (itemId: string | null) => void;
  openItem: (itemId: string) => void;
}) {
  const {
    linkMode,
    setLinkMode,
    hideLogEntries,
    setHideLogEntries,
    hideArchivedItems,
    setHideArchivedItems,
    layoutSettings,
    setLayoutSettings,
    showLayoutSettings,
    setShowLayoutSettings,
    neighbourSettings,
    setNeighbourSettings
  } = useGraphViewSettingsContext();
  const { allItems, itemById, itemLookupTree: fullItemLookupTree } = useAdventureDataContext();
  const [showHiddenItemsInfo, setShowHiddenItemsInfo] = useState(false);

  const childrenByParentId = useMemo(() => {
    const map = new Map<string, SquiggleItem[]>();
    for (const item of allItems) {
      if (item.parent) {
        const siblings = map.get(item.parent) ?? [];
        siblings.push(item);
        map.set(item.parent, siblings);
      }
    }
    return map;
  }, [allItems]);

  const graphElements = useMemo(() => {
    // We want to rerun this memo if the layout settings change, even though they aren't used here
    /* eslint-disable @typescript-eslint/no-unused-vars */
    const forceDependency = layoutSettings; 

    const isAllowedByVisibilitySettings = (item: SquiggleItem) => {
      if (hideArchivedItems && item.archived) {
        return false;
      }
      if (hideLogEntries && item.type === 'logentry') {
        return false;
      }
      return true;
    };

    const archivedItems = itemsOpen.filter((item) => item.archived);
    const candidateItems = hideArchivedItems ? itemsOpen.filter((item) => !item.archived) : itemsOpen;
    const hiddenArchivedCount = hideArchivedItems ? archivedItems.length : 0;
    const hiddenLogCount = hideLogEntries ? candidateItems.filter((item) => item.type === 'logentry').length : 0;
    const graphItems = hideLogEntries ? candidateItems.filter((item) => item.type !== 'logentry') : candidateItems;
    const openItemIds = new Set(graphItems.map((item) => item.id as ItemId));
    const sortedItems = [...graphItems].sort((left, right) => left.id.localeCompare(right.id));
    const itemLookupTree = linkMode === 'all-links' ? fullItemLookupTree : null;

    const edges = constructEdges(sortedItems, openItemIds, itemLookupTree, linkMode);
    let neighbourEdges: Edge[] = [];
    let neighbourItems: SquiggleItem[] = [];
    let neighbourItemIds = new Set<ItemId>();
    if (neighbourSettings === 'all') {
      const neighbourLinks = constructNeighbourLinks(
        allItems,
        sortedItems,
        openItemIds,
        itemById,
        childrenByParentId,
        itemLookupTree,
        linkMode,
        isAllowedByVisibilitySettings
      );
      neighbourEdges = neighbourLinks.edges;
      neighbourItems = neighbourLinks.neighbourItems;
      neighbourItemIds = new Set(neighbourItems.map((item) => item.id as ItemId));
    }
    const allEdges = [...edges, ...neighbourEdges];

    const [connectedItems, unconnectedItems] = filterConnectedItems(sortedItems, allEdges);
    const nodes = createNodesWithClusteredSpreadStartingPositions([...connectedItems, ...neighbourItems], allEdges, neighbourItemIds);

    return {
      nodes,
      edges: allEdges,
      hiddenArchivedCount,
      hiddenLogCount,
      hiddenUnconnectedCount: unconnectedItems.length,
      hiddenUnconnectedItems: unconnectedItems.sort((a, b) => a.name.localeCompare(b.name))
    };
  }, [itemsOpen, linkMode, hideLogEntries, hideArchivedItems, neighbourSettings, layoutSettings, allItems, itemById, childrenByParentId, fullItemLookupTree /* KEEP THIS! We want to re-seed and recalculate when settings change */]);

  const elements = useMemo(() => [...graphElements.nodes, ...graphElements.edges], [graphElements]);
  const hiddenItemsCount = graphElements.hiddenLogCount + graphElements.hiddenArchivedCount + graphElements.hiddenUnconnectedCount;

  const updateLayoutSetting = (key: keyof LayoutSettings, value: number) => {
    if (!Number.isFinite(value)) {
      return;
    }
    setLayoutSettings((current) => ({
      ...current,
      [key]: value
    }));
  };

  return (
    <div className="graphview">
      <div className="graphviewtoolbar">
        <div className="graphviewtoolbarrow">
          <div className="graphviewtoolbarleft">
            <div className="toolbarsegmentcontrol">
              <div className="toolbarsegmentlabel">Links</div>
              <div className="toolbarsegmented">
                <button
                  className={`toolbarsegment ${linkMode === 'parent-links' ? 'active' : ''}`}
                  onClick={() => setLinkMode('parent-links')}
                  type="button"
                >
                  Parent/child
                </button>
                <button
                  className={`toolbarsegment ${linkMode === 'explicit-links' ? 'active' : ''}`}
                  onClick={() => setLinkMode('explicit-links')}
                  type="button"
                >
                  All explicit
                </button>
                <button
                  className={`toolbarsegment ${linkMode === 'all-links' ? 'active' : ''}`}
                  onClick={() => setLinkMode('all-links')}
                  type="button"
                >
                  ALL
                </button>
              </div>
            </div>
            <div className="toolbarsegmentcontrol">
              <div className="toolbarsegmentlabel">Log entries</div>
              <div className="toolbarsegmented">
                <button
                  className={`toolbarsegment ${hideLogEntries ? 'active' : ''}`}
                  onClick={() => setHideLogEntries(true)}
                  type="button"
                >
                  Hide
                </button>
                <button
                  className={`toolbarsegment ${!hideLogEntries ? 'active' : ''}`}
                  onClick={() => setHideLogEntries(false)}
                  type="button"
                >
                  Show
                </button>
              </div>
            </div>
            <div className="toolbarsegmentcontrol">
              <div className="toolbarsegmentlabel">Archived items</div>
              <div className="toolbarsegmented">
                <button
                  className={`toolbarsegment ${hideArchivedItems ? 'active' : ''}`}
                  onClick={() => setHideArchivedItems(true)}
                  type="button"
                >
                  Hide
                </button>
                <button
                  className={`toolbarsegment ${!hideArchivedItems ? 'active' : ''}`}
                  onClick={() => setHideArchivedItems(false)}
                  type="button"
                >
                  Show
                </button>
              </div>
            </div>
            <div className="toolbarsegmentcontrol">
              <div className="toolbarsegmentlabel">Neighbours</div>
              <div className="toolbarsegmented">
                <button
                  className={`toolbarsegment ${neighbourSettings === 'none' ? 'active' : ''}`}
                  onClick={() => setNeighbourSettings('none')}
                  type="button"
                >
                  None
                </button>
                <button
                  className={`toolbarsegment ${neighbourSettings === 'all' ? 'active' : ''}`}
                  onClick={() => setNeighbourSettings('all')}
                  type="button"
                >
                  ALL
                </button>
              </div>
            </div>
            <button
              className={`toolbariconbutton ${showLayoutSettings ? 'active' : ''}`}
              onClick={() => setShowLayoutSettings((current) => !current)}
              type="button"
              aria-label="Toggle layout debug settings"
              title="Toggle layout debug settings"
            >
              <FontAwesomeIcon icon={faGears as IconProp} />
            </button>
          </div>
          <div className="graphviewtoolbarright">
            { hiddenItemsCount > 0 && (
              <div className="graphviewhiddenitems">
                <button
                  className={`graphviewstatus hiddenitemstoggle ${showHiddenItemsInfo ? 'active' : ''}`}
                  onClick={() => setShowHiddenItemsInfo((current) => !current)}
                  type="button"
                  aria-label="Toggle hidden items details"
                  title="Hidden items details"
                >
                  <span className="hiddenitemswarningicon" aria-hidden="true">
                    <FontAwesomeIcon icon={faTriangleExclamation as IconProp} />
                  </span>
                  <span>Hidden: {hiddenItemsCount}</span>
                </button>
                {showHiddenItemsInfo && (
                  <div className="hiddenitemsdropdown">
                    {graphElements.hiddenLogCount > 0 && (
                      <div className="hiddenitemsdropdownrow">Log entries hidden: {graphElements.hiddenLogCount}</div>
                    )}
                    {graphElements.hiddenArchivedCount > 0 && (
                      <div className="hiddenitemsdropdownrow">Archived items hidden: {graphElements.hiddenArchivedCount}</div>
                    )}
                    {graphElements.hiddenUnconnectedCount > 0 && (
                      <>
                        <div className="hiddenitemsdropdownrow">Unconnected items hidden ({graphElements.hiddenUnconnectedCount}):</div>
                        <div className="hiddenitemslisting">
                          {graphElements.hiddenUnconnectedItems.map((item) => (
                            <div key={item.id} className="hiddenitemslistingrow">
                              {item.name}
                            </div>
                          ))}
                        </div>
                        <div className="hiddenitemsdropdownrow">To reduce clutter, open items are hidden if they are not connected to any other visible item.</div>
                      </>
                    )}
                    {hiddenItemsCount === 0 && (
                      <div className="hiddenitemsdropdownrow">No hidden items.</div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        {showLayoutSettings && (
          <div className="graphviewtoolbarrow">
            <div className="graphviewtoolbarleft">
              <div className="toolbarlayoutsettings">
                <label className="toolbarsetting">
                  <span className="toolbarsettinglabel">Node repulsion</span>
                  <input
                    className="toolbarsettinginput wide"
                    type="number"
                    value={layoutSettings.nodeRepulsion}
                    step={100}
                    min={0}
                    onChange={(event) => updateLayoutSetting('nodeRepulsion', Number(event.target.value))}
                  />
                </label>
                <label className="toolbarsetting">
                  <span className="toolbarsettinglabel">Edge length hint</span>
                  <input
                    className="toolbarsettinginput"
                    type="number"
                    value={layoutSettings.idealEdgeLength}
                    step={10}
                    min={0}
                    onChange={(event) => updateLayoutSetting('idealEdgeLength', Number(event.target.value))}
                  />
                </label>
                <label className="toolbarsetting">
                  <span className="toolbarsettinglabel">Edge elasticity</span>
                  <input
                    className="toolbarsettinginput"
                    type="number"
                    value={layoutSettings.edgeElasticity}
                    step={0.1}
                    min={0}
                    onChange={(event) => updateLayoutSetting('edgeElasticity', Number(event.target.value))}
                  />
                </label>
                <label className="toolbarsetting">
                  <span className="toolbarsettinglabel">Initial energy</span>
                  <input
                    className="toolbarsettinginput"
                    type="number"
                    value={layoutSettings.initialEnergyOnIncremental}
                    step={0.05}
                    min={0}
                    onChange={(event) => updateLayoutSetting('initialEnergyOnIncremental', Number(event.target.value))}
                  />
                </label>
                <button
                  className="toolbarsettingreset"
                  onClick={() => setLayoutSettings({ ...defaultLayoutSettings })}
                  type="button"
                >
                  Reset
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      <GraphCanvas elements={elements} openItem={openItem} layoutSettings={layoutSettings} />
    </div>
  );
}

export default GraphView;
